import React from 'react';
import { Box, Typography } from '@mui/material';
import { RestockV2Plan, RestockV2ListVariant } from './types';
import { C } from './tokens';

type EvidenceVariant = RestockV2ListVariant & {
  ordered?: number; online_units30?: number; counter_sold30?: number;
  historical_units365?: number; ranking_basis?: string | null;
};
const quantity = (value: number | null | undefined) => value == null ? '—' : value;

export default function AssortmentOrderOverview({ plan }: { plan: RestockV2Plan }) {
  const approved = plan.list?.approved || [];
  const missing = plan.list?.missing || [];
  if (!approved.length && !missing.length) return null;
  const statuses = new Map<string, string>();
  (plan.accounting?.buckets || []).forEach((bucket) =>
    bucket.skus.forEach((sku) => statuses.set(sku, bucket.label)));
  const orderVariants = new Map((plan.order?.groups || []).flatMap((group) => group.lines)
    .flatMap((line) => (line.variants || []).map((variant) =>
      [`${line.sku}|${variant.variant_id}`, variant] as const)));
  return <Box component="section" aria-label="全部選品追蹤" sx={{ mb: 2 }}>
    <Typography component="h2" sx={{ fontSize: 16, fontWeight: 800, mb: 1 }}>
      全部選品 · {approved.length + missing.length} 款
    </Typography>
    <Typography sx={{ fontSize: 12, color: C.ink2, mb: 1 }}>每款都保留；本次需採購的色碼可在下方調整。衣架容量不限制訂貨。</Typography>
    {approved.map((row) => <Box component="details" key={row.sku} sx={{ borderBottom: '1px solid', borderColor: C.line, py: 1 }}>
      <Box component="summary" sx={{ cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
        {row.sku} {row.title} · {row.all_variants_vetoed ? '全色碼排除' : statuses.get(row.sku) || '待確認安排'}
      </Box>
      <Typography sx={{ fontSize: 12, mt: 1 }}>
        {row.top_variant ? `銷售建議：${row.top_variant.color || ''} ${row.top_variant.size || ''}（${row.top_variant.basis === 'mix30' ? '近 30 天' : row.top_variant.basis === 'own_year' ? '近一年' : '同類試賣推估'}）` : '尚無銷售建議'}
        {' · 你指定：'}{(row.main_variants || (row.main_variant ? [row.main_variant] : [])).map((v) => `${v.color || ''} ${v.size || ''}`).join('、') || '未指定'}
      </Typography>
      {(row.variants || []).map((base) => {
        const variant = base as EvidenceVariant;
        const order = orderVariants.get(`${row.sku}|${variant.variant_id}`);
        return <Box key={variant.variant_id} sx={{ mt: 0.75, fontSize: 12 }}>
          <b>{variant.color} {variant.size}</b>
          {' · '}{variant.vetoed ? '已排除' : variant.orderable === false ? variant.block_reason || '不可訂' : `本次採購 ${order?.order_qty || 0} 件`}
          <div>目標 {quantity(variant.target)} · 門市 {quantity(variant.on_hand)} · 辦公室 {quantity(variant.office)} · 運送中 {quantity(variant.transit)} · 已訂未收 {quantity(order?.ordered ?? variant.ordered)}</div>
          {variant.ranking_basis === 'mix30' && <div>近 30 天：官網 {quantity(variant.online_units30)} 件、門市 {quantity(variant.counter_sold30)} 件</div>}
          {variant.ranking_basis === 'own_year' && <div>近一年：{quantity(variant.historical_units365)} 件</div>}
          {variant.ranking_basis === 'category' && <div>此款尚無銷售，依同類推估</div>}
        </Box>;
      })}
    </Box>)}
    {missing.map((sku) => <Typography key={sku} sx={{ py: 1, fontSize: 13 }}>{sku} · 商品資料缺失，需確認是否下架；本次不可訂</Typography>)}
  </Box>;
}
